import {
  selectOrganizationId,
  useUpdateTourFrameStepsMutation,
} from '@carboncopyco/data';
import {
  type TourFrame,
  type TourStep,
  TourStepAddDirection,
} from '@carboncopyco/types';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { cloneDeep } from 'lodash-es';
import type React from 'react';
import { useSelector } from 'react-redux';
import { useTourDispatch, useTourSelector } from '../../../../../../hooks';
import { useNotify } from '../../../../../../hooks/useNotify';
import {
  selectDraftSettings,
  setActiveStepId,
  setAddingStepInProgress,
} from '../../../../../../store';
import { useTourEditorPageProps } from '../../../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../../../ToursReplayerEditor';
import useFrameWarnings from '../useFrameWarnings';

interface Props {
  stepId: string;
  tourId: number;
  frame: TourFrame;
  frames: TourFrame[];
  replayerRef: React.RefObject<ReplayerEditorRef>;
  doReplay: (step: TourStep) => void;
  onStepSelect: () => void;
}

const useStepActions = ({
  stepId,
  tourId,
  frame,
  frames,
  replayerRef,
  doReplay,
  onStepSelect,
}: Props) => {
  const { id: frameId } = frame;
  const dispatch = useTourDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const [updateTourSteps] = useUpdateTourFrameStepsMutation();
  const { uuid, rootElement } = useTourEditorPageProps();
  const { showNotify, closeNotify } = useNotify(rootElement);
  const { trackEvent } = useAnalytics();
  const draftSettings = useTourSelector(selectDraftSettings(uuid));
  const { removeWarning, calculateAndStoreStepWarning } = useFrameWarnings();

  const handleStepClick = (step: TourStep) => {
    dispatch(setActiveStepId({ uuid, stepId: step.stepId }));
    doReplay(step);
    onStepSelect();
    closeNotify();
  };

  const handleDeleteStep = () => {
    const previousSteps = frame.steps;
    const newSteps = frame.steps.filter((step) => step.stepId !== stepId);
    updateTourSteps({
      organizationId,
      tourId,
      frameId,
      steps: newSteps,
    });
    replayerRef.current.deleteStep();
    replayerRef.current.removeHighlight();
    detachRelatedSteps(stepId);

    removeWarning(frameId, stepId, 'position');

    showNotify({
      message: 'The step was deleted',
      onActionClick: () => restoreStep(stepId, previousSteps),
      actionText: 'Restore',
    });
    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_DELETE_STEP, {
      location: 'tour_editor_frame',
      tourId: frame.tourData.id,
      frameId: frame.id,
      stepId: stepId,
    });
  };

  const detachRelatedSteps = (stepId: TourStep['stepId']) => {
    const result = frames.reduce((acc, frame) => {
      let shouldAdd = false;
      const updatedSteps = frame.steps.map((step) => {
        const { nextButton, previousButton, lastStepButton } =
          step.config.tooltip.buttons;
        let newStep = step;
        if (nextButton?.action.goToStepId === stepId) {
          shouldAdd = true;
          newStep = cloneDeep(newStep);
          newStep.config.tooltip.buttons.nextButton.action.name = 'nextStep';
          newStep.config.tooltip.buttons.nextButton.action.goToStepId = null;
        }
        if (previousButton?.action.goToStepId === stepId) {
          shouldAdd = true;
          newStep = cloneDeep(newStep);
          newStep.config.tooltip.buttons.previousButton.action.name =
            'previousStep';
          newStep.config.tooltip.buttons.previousButton.action.goToStepId =
            null;
        }
        if (lastStepButton?.action.goToStepId === stepId) {
          shouldAdd = true;
          newStep = cloneDeep(newStep);
          newStep.config.tooltip.buttons.lastStepButton.action.name =
            'startOverTour';
          newStep.config.tooltip.buttons.previousButton.action.goToStepId =
            null;
        }
        return newStep;
      });
      if (shouldAdd) {
        return [
          ...acc,
          {
            tourId: frame.tourData.id,
            frameId: frame.id,
            steps: updatedSteps,
          },
        ];
      }
      return acc;
    }, []);
    result.forEach((res) => {
      updateTourSteps(res);
    });
  };

  const restoreStep = (stepId: string, previousSteps: TourStep[]) => {
    dispatch(setAddingStepInProgress({ uuid, inProgress: false }));
    replayerRef.current.cancelInspectMode();
    updateTourSteps({
      organizationId,
      tourId,
      frameId,
      steps: previousSteps,
    })
      .unwrap()
      .then((frame) => {
        replayerRef.current?.playStep(stepId);
        const step = frame.steps.find((step) => step.stepId === stepId);
        calculateAndStoreStepWarning(
          frameId,
          step,
          draftSettings?.showTooltips
        );
      });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_RESTORE_STEP, {
      location: 'tour_editor_frame',
      tourId: frame.tourData.id,
      frameId: frame.id,
      stepId: stepId,
    });
  };

  const handleAddStepAbove = () => {
    handleOnAddStep(TourStepAddDirection.above);
  };

  const handleAddStepBellow = () => {
    handleOnAddStep(TourStepAddDirection.below);
  };

  const handleOnAddStep = (direction: TourStepAddDirection) => {
    const previousSteps = frame.steps;
    replayerRef?.current?.addStep(frame, direction, stepId);
    dispatch(setAddingStepInProgress({ uuid, inProgress: true }));

    showNotify({
      message: 'Inspect the element you want the tooltip to be attached to',
      onActionClick: () => restoreStep(stepId, previousSteps),
      actionText: 'Cancel',
    });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_ADD_STEP_TO_FRAME, {
      location: 'tour_editor_frame',
      tourId: frame.tourData.id,
      frameId: frame.id,
      stepId: stepId,
      variables: {
        direction,
      },
    });
  };

  return {
    handleStepClick,
    handleDeleteStep,
    handleAddStepAbove,
    handleAddStepBellow,
  };
};

export default useStepActions;
